/**
 * `register-vendor-party` — FR-14, FR-29, IR-03 (interim), CR-01. R-76-2
 * (Gerry, 11 Sep 2026) · CLM-78.
 *
 * *"If the contract type is vendor, the user can provide the name of the
 * vendor."* Vendors are meant to arrive from Dynamics (IR-03) and no sync exists,
 * so until the adapter does a Vendor party may be **registered in CLM from its
 * typed legal name**. The body carries the name and nothing else:
 *
 *  - the key is minted server-side from the reserved `V…` space
 *    (`mintVendorKey`, FR-14 — never typed, never in the request);
 *  - the row is `sor_system = 'CLM'`, `sor_reference_id = party_key` (A3 — the
 *    later Dynamics adapter overwrites both in place, so `contract_party` never
 *    moves) and `registered_by_upn` = the caller (migration 0090);
 *  - the typed name becomes the first **confirmed `Manual` alias**, so FR-29
 *    substitution works from the first document.
 *
 * Two writes, two `tg_party_*_audit` rows (rule 7). Duplicate check by
 * normalised name **and** alias (`normaliseVendorName`) → 409 `vendor_exists`
 * with the existing party offered for reuse. Gate `party:register_vendor`
 * (contributor+, A1).
 */
import { z } from 'zod';
import { PartyAliasSchema } from '../domain/party.js';
import { AuditReceiptSchema } from '../provenance.js';
import { SelectablePartySchema } from './upload.js';
export const RegisterVendorPartyRequestSchema = z
    .object({
    /** The vendor's legal name as typed. Trimmed server-side; the key is minted from it. */
    display_name: z.string().min(2).max(200),
})
    .strict();
export const RegisterVendorPartyResponseSchema = z.object({
    /** `party_key` minted, `party_type 'Vendor'` — selectable in the picker at once. */
    party: SelectablePartySchema,
    /** The confirmed `Manual` alias = the typed name. */
    alias: PartyAliasSchema,
    /** The `tg_party_alias_audit` row of the alias write — the NFR-13 receipt. */
    audit: AuditReceiptSchema,
});
/** The two 409 bodies. `vendor_exists` offers the existing party for reuse. */
export const RegisterVendorPartyConflictSchema = z.discriminatedUnion('code', [
    z.object({ code: z.literal('vendor_exists'), party: SelectablePartySchema }).strict(),
    z.object({ code: z.literal('vendor_key_space_exhausted') }).strict(),
]);
/** Request-time only — never stored; vendor-ness is `party.party_type` (K5). */
export const COUNTERPARTY_KINDS = ['client', 'vendor'];
export const CounterpartyKindSchema = z.enum(COUNTERPARTY_KINDS);
/** The queue row the hold writes when the in-tenant leak scan finds an unconfirmed counterparty spelling (B4). */
export const COUNTERPARTY_NAME_FIELD = 'parties.primary.counterparty_name';
//# sourceMappingURL=register-vendor-party.js.map